import React from 'react';
import {
  ArchiveBoxIcon,
  TruckIcon,
  TicketIcon,
  StarIcon,
  ClockIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '../ui/Tooltip';
import { abbreviateLocationName } from './utils';
import { MoveRisk, MoveGroup, ProjectedStatus } from './types';
import styles from './SuperCompactMoveCard.module.css';
import { RouteEta } from './RouteEta';
import type { RouteCascadeEstimate } from './routeEtaCascade';

export interface SuperCompactMoveCardProps {
  move: MoveRisk;
  routeCascade?: RouteCascadeEstimate;
  projectedStatus?: ProjectedStatus;
  projectedETA?: string;
  isETAToDestination?: boolean;
  onProClick?: (pronumber: number, legnum: number) => void;
  group?: MoveGroup;
  onDragStart?: (e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  fontSize?: number;
  showChassis?: boolean;
}

const STATUS_LETTER: Record<string, string> = {
  Dispatched: 'D', Started: 'S', 'In Transit': 'T',
  'At Origin': 'O', 'At Destination': 'A', Completed: 'C',
};

const STATUS_LABEL: Record<string, string> = {
  Dispatched: 'Not Started', Started: 'Started', 'In Transit': 'In Transit',
  'At Origin': 'At Origin', 'At Destination': 'At Destination', Completed: 'Completed',
};

const STATUS_STRIP_CLASS: Record<string, string> = {
  Dispatched: 'statusStripDispatched', Started: 'statusStripStarted', 'In Transit': 'statusStripInTransit',
  'At Origin': 'statusStripAtOrigin', 'At Destination': 'statusStripAtDestination', Completed: 'statusStripCompleted',
};

const RISK_BORDER: Record<string, string> = {
  at_risk: '#f59e0b', will_miss: '#ef4444', missed: '#ef4444',
};

const KEY_ACCOUNT_COLOR: Record<string, string> = {
  'KEY ACCOUNTS 1': '#ef4444',
  'KEY ACCOUNTS 2': '#f97316',
  'KEY ACCOUNTS 3': '#eab308',
  'KEY ACCOUNTS 4': '#22c55e',
  'CONTRACT':       '#84cc16',
  'KEY PROSPECT 1': '#a855f7',
  'KEY PROSPECT 2': '#a855f7',
  'KEY PROSPECT 3': '#a855f7',
  'KEY PROSPECT 4': '#a855f7',
  'HYPER FOCUS':    '#3b82f6',
};

const RISK_COLOR: Record<string, string> = {
  on_track: '#22c55e', at_risk: '#f59e0b', will_miss: '#ef4444', missed: '#ef4444',
};

export const SuperCompactMoveCard: React.FC<SuperCompactMoveCardProps> = ({
  move,
  routeCascade,
  projectedStatus,
  projectedETA,
  isETAToDestination,
  onProClick,
  group,
  onDragStart,
  onDragEnd,
  fontSize = 9,
  showChassis,
}) => {
  const isNotStarted = move.status === 'Dispatched';
  const isDraggable = isNotStarted && !!onDragStart;
  const borderColor = RISK_BORDER[projectedStatus ?? ''] ?? '#22c55e';
  const riskColor = RISK_COLOR[projectedStatus ?? 'on_track'] ?? '#22c55e';
  const stripClass = STATUS_STRIP_CLASS[move.status] ?? 'statusStripDispatched';

  const openTickets = (move.trouble_tickets ?? []).filter(
    t => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING'
  );

  return (
    <div
      className={`${styles.card} ${(move.reschedule_count ?? 0) > 0 ? styles.cardRescheduled : ''} ${(move.matches_dispatcher_filter === false || move.matches_region_filter === false) ? styles.filteredOut : ''}`}
      style={{
        borderLeftColor: borderColor,
        cursor: isDraggable ? 'grab' : 'default',
        fontSize: `${fontSize}px`,
      }}
      draggable={isDraggable}
      onDragStart={isDraggable ? (e) => onDragStart!(e, move, move.driver, group) : undefined}
      onDragEnd={onDragEnd}
    >
      {/* Left status strip — colored band with letter, tooltip on hover */}
      <Tooltip
        content={`${STATUS_LABEL[move.status] ?? move.status} — ${move.pronumber}-${move.legnum}`}
        placement="right"
      >
        <button
          className={`${styles.statusStrip} ${styles[stripClass]}`}
          onClick={(e) => { e.stopPropagation(); onProClick?.(move.pronumber, move.legnum); }}
        >
          {STATUS_LETTER[move.status] ?? '?'}
        </button>
      </Tooltip>

      {/* Card content: icon row + route row */}
      <div className={styles.cardContent}>
        {move.status !== 'Completed' && <RouteEta moveStatus={move.status} data={move.route_eta} cascade={routeCascade} />}

        {/* Line 1 — Icon row */}
        <div className={styles.iconRow}>

          {/* ETA / risk */}
          {projectedETA && move.status !== 'Completed' && (
            <Tooltip content={`ETA ${projectedETA} ${isETAToDestination ? 'to Dest' : 'to Orig'}`} placement="top">
              <span className={styles.etaDot} style={{ color: riskColor }}>
                {projectedETA}
              </span>
            </Tooltip>
          )}

          {/* Import / Export */}
          <Tooltip content={move.movetype === 'IMPORT' ? 'Import' : 'Export'} placement="top">
            <span className={`${styles.badge} ${move.movetype === 'IMPORT' ? styles.badgeImport : styles.badgeExport}`}>
              {move.movetype === 'IMPORT' ? 'I' : 'E'}
            </span>
          </Tooltip>

          {/* Live / Drop */}
          <Tooltip content={move.legdrop === 'Y' ? 'Drop' : 'Live'} placement="top">
            <span className={`${styles.badge} ${move.legdrop === 'Y' ? styles.badgeDrop : styles.badgeLive}`}>
              {move.legdrop === 'Y' ? 'D' : 'L'}
            </span>
          </Tooltip>

          {/* Size */}
          {move.size && (
            <Tooltip content={`Size: ${move.size}`} placement="top">
              <span className={styles.infoIcon}>
                <TagIcon style={{ width: '1.1em', height: '1.1em' }} />
                <span className={styles.infoIconLabel}>{move.size}</span>
              </span>
            </Tooltip>
          )}

          {/* Container */}
          {move.container && (
            <Tooltip content={`Container: ${move.container}`} placement="top">
              <span className={styles.infoIcon}>
                <ArchiveBoxIcon style={{ width: '1.1em', height: '1.1em' }} />
              </span>
            </Tooltip>
          )}

          {/* Chassis */}
          {showChassis && move.chassisid && (
            <Tooltip content={`Chassis: ${move.chassisid}${move.chassistype ? ` (${move.chassistype})` : ''}`} placement="top">
              <span className={styles.infoIcon}>
                <TruckIcon style={{ width: '1.1em', height: '1.1em' }} />
              </span>
            </Tooltip>
          )}

          {/* Appointment / deadline time */}
          {(move.appointment_time || move.deadline_time) && (
            <Tooltip
              content={
                <div className="text-xs space-y-1">
                  {move.appointment_time && <div>Appt: {move.appointment_time}</div>}
                  {move.deadline_time && <div>{move.deadline_type ?? 'Deadline'}: {move.deadline_time}</div>}
                  {move.deadline_date && <div className="text-gray-400">{move.deadline_date}</div>}
                </div>
              }
              placement="top"
            >
              <span className={styles.infoIcon}>
                <ClockIcon style={{ width: '1.1em', height: '1.1em' }} />
              </span>
            </Tooltip>
          )}

          {/* Travel time to next move */}
          {move.travel_to_next_minutes != null && move.travel_to_next_minutes > 0 && (
            <Tooltip content={`${move.travel_to_next_minutes}min to next move`} placement="top">
              <span className={`${styles.infoIcon} ${move.travel_to_next_minutes > 60 ? styles.infoIconWarn : ''}`}>
                <ClockIcon style={{ width: '1.1em', height: '1.1em' }} />
              </span>
            </Tooltip>
          )}

          {/* Key Account */}
          {move.key_account && move.key_account.toUpperCase() !== 'AVERAGE' && (
            <Tooltip content={move.key_account === 'Y' ? 'Key Account' : move.key_account} placement="top">
              <span className={styles.infoIcon} style={{ color: KEY_ACCOUNT_COLOR[move.key_account.toUpperCase()] ?? '#f59e0b' }}>
                <StarIcon style={{ width: '1.1em', height: '1.1em' }} />
              </span>
            </Tooltip>
          )}

          {/* At Risk flag */}
          {move.risk_factor === 'recovery_time' && (
            <Tooltip content="Recovery Time Risk" placement="top">
              <span className={`${styles.flagIcon} ${styles.flagRisk}`}>!</span>
            </Tooltip>
          )}

          {/* Hazmat */}
          {move.requires_hazmat && (
            <Tooltip content="Hazmat" placement="top">
              <span className={`${styles.flagIcon} ${styles.flagHazmat}`}>☢</span>
            </Tooltip>
          )}

          {/* Tank */}
          {move.requires_tank && (
            <Tooltip content="Tank Endorsement Required" placement="top">
              <span className={`${styles.flagIcon} ${styles.flagTank}`}>T</span>
            </Tooltip>
          )}

          {/* 93K */}
          {move.requires_93k && (
            <Tooltip content="93K Required" placement="top">
              <span className={`${styles.flagIcon} ${styles.flag93k}`}>93</span>
            </Tooltip>
          )}

          {/* Trouble tickets */}
          {openTickets.length > 0 && (
            <Tooltip
              content={
                <div className="text-xs space-y-1 max-w-xs">
                  <div className="font-semibold">Trouble Tickets ({openTickets.length})</div>
                  {openTickets.map(t => (
                    <div key={t.recno}>{t.category || 'No Category'} — {t.status}</div>
                  ))}
                </div>
              }
              placement="top"
            >
              <span className={styles.infoIconTT}>
                <TicketIcon style={{ width: '1.1em', height: '1.1em' }} />
                <span className={styles.infoIconLabel}>{openTickets.length}</span>
              </span>
            </Tooltip>
          )}

          {/* New Location (origin or dest) */}
          {(move.orig_is_new_location === 'Y' || move.dest_is_new_location === 'Y') && (
            <Tooltip content="New Location" placement="top">
              <span className={`${styles.flagIcon} ${styles.flagNew}`}>N</span>
            </Tooltip>
          )}

        </div>

        {/* Line 2 — Origin > Dest (tooltip shows full names + cities) */}
        <Tooltip
          content={
            <div className="text-xs space-y-1 max-w-xs">
              <div className="font-semibold">{move.pronumber}-{move.legnum}</div>
              <div>{move.origin_name}</div>
              <div className="text-gray-400">{move.origin_city_state}</div>
              <div className="text-gray-300">↓</div>
              <div>{move.dest_name}</div>
              <div className="text-gray-400">{move.dest_city_state}</div>
              {move.deadline_time && <div className="text-gray-300 pt-0.5 border-t border-gray-600">{move.deadline_time}</div>}
            </div>
          }
          placement="bottom"
        >
          <div className={styles.routeRow}>
            {abbreviateLocationName(move.origin_name)} → {abbreviateLocationName(move.dest_name)}
          </div>
        </Tooltip>

      </div>
    </div>
  );
};

export default SuperCompactMoveCard;
